/**
 * A link between a SystmOne visit and a Doc Abode job
 */
export type LinkedVisit = {
    /**
     * The ID of the SystmOne visit as generated by the SystmOne software
     */
    s1VisitId: string;
    /**
     * Date and time the link was created
     */
    createdAt: string;
    /**
     * The ID of the Doc Abode job
     */
    jobId: string;
    /**
     * The ID of the personJob that is linked to the S1 visit, as per the v2 API
     * When using a v1 job, this is '0' for HCP1 and '1' for HCP2
     */
    personJobId: string;
    /**
     * The version of the job at the time the link was created
     */
    version: number;
    /**
     * Whether the link has been deleted
     * It can be deleted when the visit is linked to a different version of the job, so is no longer
     * relevant
     */
    isDeleted: boolean;
};

/**
 * A referral as it is when pulled from SystmOne, with the attributes nested
 */
export type S1Referral = {
    ActionDate: string;
    Code: Record<string, string>;
    EndDate: Record<string, string> | string;
    Location: Record<string, string>;
    Outcome: string;
    Reason: string;
    RecordedAt: string;
    ReferralDate: string;
    ReferralSource: string;
    ReferralUID: string;
    Service: string;
    Status: string;
    Urgency: string;
    _attributes: {
        DateTime: string;
        DoneAt: string;
        DoneBy: string;
        EventUID: string;
        UserName: string;
    };
};

/**
 * SystmOne referrals grouped by NHS number
 */
export type S1Referrals = {
    [nhsNumber: string]: {
        /**
         * The list of SystmOne referrals
         */
        referrals: S1Referral[];
        /**
         * Date and time the referrals were last synced with SystmOne
         */
        updatedAt: string;
    };
};

export enum S1EventType {
    VISIT = 'VISIT',
    REFERRALS = 'REFERRALS',
}

export enum S1EventName {
    SYNC = 'SYNC',
    DELETE = 'DELETE',
}

/**
 * Used to express the status of messages between Doc Abode and S1
 */
export enum S1Status {
    PENDING = 'PENDING',
    SUCCESS = 'SUCCESS',
    ERROR = 'ERROR',
}

/**
 * Used to express the sync status of a Doc Abode job to S1
 * String value is used across the Controller App and export of job timings
 */
export enum S1JobSyncStatus {
    DO_NOT_SYNC = 'N/A',
    LOADING = 'Checking status',
    SYNCED = 'Synced',
    NOT_SYNCED = 'Not synced',
}

/**
 * SystmOne API function request parameters
 * For more details see:
 * https://tpp-uk.com/wp-content/uploads/2023/04/SystmOne-Client-Integration-API.pdf
 */

/**
 * As the payload for the SystmOne API is converted to XML, sometimes we need to send an empty
 * object as a value.
 * E.g. `Visits: {}` is converted to `<Visits></Visits>`
 */
type EmptyObject = Record<string, never>;

/**
 * GetOrganisationMeta function parameters
 */
export type GetOrganisationMetaDataRequest = {
    LoggedOnUserOnly?: EmptyObject;
};

/**
 * GetDiary function parameters
 */
export type GetDiaryRequest = {
    MinDateTime?: string;
    MaxDateTime?: string;
    UserName?: string;
    SiteName?: string;
    Visits?: EmptyObject;
};

/**
 * GetPatientRecord function parameters
 */
export type GetPatientRecordRequest = {
    Identity?: {
        NhsNumber: string;
    };
    LeaveRecordOpen?: EmptyObject;
    Filter?: {
        MinEffectiveDate?: string;
        MaxEffectiveDate?: string;
        MinEntryDate?: string;
        MaxEntryDate?: string;
        Referrals?: EmptyObject;
        Visits?: EmptyObject;
    };
};

export type S1Visit = {
    Status: string;
    UserName: string;
    DateTime?: string;
    Duration?: string;
    LinkedReferralUID?: string;
    VisitUID?: string;
};

/**
 * UpdatePatientRecord function parameters
 */
export type UpdatePatientRecordRequest = {
    Identity: {
        NhsNumber: string;
    };
    NonClinical?: {
        Visit?: S1Visit;
    };
};

/**
 * DeletePatientRecord function parameters
 */
export type DeletePatientRecordRequest = {
    Identity: {
        NhsNumber: string;
    };
    VisitUID?: string;
    Reason?: string;
};

export type ClientIntegrationRequestFunctionParameters =
    | GetOrganisationMetaDataRequest
    | GetDiaryRequest
    | GetPatientRecordRequest
    | UpdatePatientRecordRequest
    | DeletePatientRecordRequest;

/**
 * The function, version and parameters to send to the SystmOne API
 */
export type ClientIntegrationRequest<
    T extends ClientIntegrationRequestFunctionParameters = ClientIntegrationRequestFunctionParameters
> = {
    APIKey: string;
    RequestUID: string;
    DeviceVersion: string;
    DeviceID?: string;
    Function: string;
    FunctionVersion: string;
    FunctionParameters: T;
};

/**
 * SystmOne API function response parameters
 */

export type GetOrganisationMetaDataUser = {
    UserName: string;
};

/**
 * GetOrganisationMeta response
 */
export type GetOrganisationMetaDataResponse = {
    User: GetOrganisationMetaDataUser | GetOrganisationMetaDataUser[];
};

export type GetDiaryResult = {
    Visit?: S1Visit;
};

/**
 * GetDiary response
 */
export type GetDiaryResponse = {
    Result: GetDiaryResult | GetDiaryResult[];
};

export type S1ReferralIn = {
    ActionDate: string;
    Code: Record<string, string>;
    EndDate: Record<string, string>;
    Location: Record<string, string>;
    Outcome: string;
    Reason: string;
    RecordedAt: string;
    ReferralDate: string;
    ReferralSource: string;
    ReferralUID: string;
    Service: string;
    Status: string;
    Urgency: string;
};

export type S1ReferralAttributes = {
    DateTime: string;
    DoneAt: string;
    DoneBy: string;
    EventUID: string;
    UserName: string;
};

export type GetPatientRecordEvent = {
    ReferralIn?: S1ReferralIn;
    _attributes: S1ReferralAttributes;
};

/**
 * GetPatientRecord response
 */
export type GetPatientRecordResponse = {
    Identity: {
        NhsNumber: string;
    };
    Clinical?: {
        Event?: GetPatientRecordEvent | GetPatientRecordEvent[];
    };
};

/**
 * UpdatePatientRecord response
 */
export type UpdatePatientRecordResponse = {
    Identity: {
        NhsNumber: string;
    };
    RecordState: string;
    VisitUID: string;
};

/**
 * DeletePatientRecord response
 */
export type DeletePatientRecordResponse = {
    Identity: {
        NhsNumber: string;
    };
    RecordState: string;
};

export type ClientIntegrationResponseBody =
    | GetOrganisationMetaDataResponse
    | GetDiaryResponse
    | GetPatientRecordResponse
    | UpdatePatientRecordResponse
    | DeletePatientRecordResponse;

/**
 * The full response received from the SystmOne API
 */
export type ClientIntegrationResponse<
    T extends ClientIntegrationResponseBody = ClientIntegrationResponseBody
> = {
    APIKey: string;
    DeviceID: string;
    DeviceVersion: string;
    RequestUID: string;
    Function: string;
    FunctionVersion: string;
    Response: T & {
        ErrorMessage?: string;
        PermanentError?: 'true' | 'false';
    };
    ResponseUID: string;
};

/**
 * A message that is sent to SystmOne from Doc Abode to perform some action on the SystmOne API
 */
export type S1Message = {
    /**
     * Date and time the message was created
     */
    createdAt: string;
    /**
     * A unique identifier for the message
     */
    requestId: string;
    /**
     * The type of event that the message is for
     */
    eventType: S1EventType;
    /**
     * The name of the event
     */
    eventName: S1EventName;
    /**
     * The status of the message
     */
    status: S1Status;
    /**
     * The request body sent to the SystmOne API
     */
    requestBody: ClientIntegrationRequest;
    /**
     * The response body received from the SystmOne API
     */
    responseBody: ClientIntegrationResponse;
    /**
     * The SystmOne user ID for the user that was logged in to SystmOne when the message was created
     */
    s1LoggedInUser: string;
};
